import { useState, useEffect, useCallback } from 'react';
import { getRegistryType, listEntries, listEntryKinds, getEntryById } from '../../../api/metaRegistriesApi';
import type { MetaRegistryType, MetaRegistryEntry, MetaRegistryEntryKind } from '../../../types/systems';

export interface RegistryBrowserState {
  registryType: MetaRegistryType | null;
  kinds: MetaRegistryEntryKind[];
  entries: MetaRegistryEntry[];
  currentEntry: MetaRegistryEntry | null;
  ancestors: MetaRegistryEntry[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useRegistryBrowser(projectId: string, slug: string, currentEntryId: string | null): RegistryBrowserState {
  const [registryType, setRegistryType] = useState<MetaRegistryType | null>(null);
  const [kinds, setKinds] = useState<MetaRegistryEntryKind[]>([]);
  const [entries, setEntries] = useState<MetaRegistryEntry[]>([]);
  const [currentEntry, setCurrentEntry] = useState<MetaRegistryEntry | null>(null);
  const [ancestors, setAncestors] = useState<MetaRegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick(t => t + 1), []);

  // Build ancestor chain by walking parent_id up (client-side, up to 10 levels)
  const buildAncestors = useCallback(async (entry: MetaRegistryEntry): Promise<MetaRegistryEntry[]> => {
    const chain: MetaRegistryEntry[] = [];
    let current = entry;
    for (let i = 0; i < 10 && current.parent_id; i++) {
      try {
        const parent = await getEntryById(projectId, slug, current.parent_id);
        chain.unshift(parent);
        current = parent;
      } catch {
        break;
      }
    }
    return chain;
  }, [projectId, slug]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [rt, ks] = await Promise.all([
          getRegistryType(projectId, slug),
          listEntryKinds(projectId, slug),
        ]);
        if (cancelled) return;
        setRegistryType(rt);
        setKinds(ks);

        let entry: MetaRegistryEntry | null = null;
        let chain: MetaRegistryEntry[] = [];

        if (currentEntryId) {
          entry = await getEntryById(projectId, slug, currentEntryId);
          if (!cancelled && entry) {
            chain = await buildAncestors(entry);
          }
        }

        if (cancelled) return;
        setCurrentEntry(entry);
        setAncestors(chain);

        const ents = await listEntries(projectId, slug, currentEntryId
          ? { parent_id: currentEntryId }
          : { all: true }
        );
        if (!cancelled) setEntries(ents);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projectId, slug, currentEntryId, buildAncestors, tick]);

  return { registryType, kinds, entries, currentEntry, ancestors, loading, error, reload };
}
